import { useEffect, useRef, useState } from 'react'
import { supabase } from '@/lib/supabase/client'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { DataTable, DataTableColumn } from '@/components/ui/data-table'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Progress } from '@/components/ui/progress'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Database, Link2, X, Plus, RefreshCw, Building2, Search, Square, CheckCircle2, XCircle, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { pipefyApi } from '@/lib/api-client'

// IDs fixos dos databases no Pipefy
const DATABASE_TIPO_DESPESA_ID = '-2v28bXC' // Database de tipos de despesa
const DATABASE_CENTRO_CUSTO_ID = 'bvoPUmtK' // Database de centros de custo

interface SubtipoDespesa {
  centro_custo_id: string | null
  centro_resultado: string
  centro_resultado_original: string
  subtipo_despesa: string
  quantidade_registros: number
  valor_total: number
}

interface CampoConfig {
  label: string
  type: string
  required: boolean
  description?: string
  connectedRepoId?: string
}

interface DatabaseItem {
  id: string
  name: string
  description: string | null
  url: string
  tags?: string[]
}

interface SubtipoDatabaseVinculado {
  id: string
  centro_custo_id: string
  pipefy_table_id: string
  pipefy_table_name: string | null
}

interface SubtipoSincronizado {
  id: string
  centro_custo_id: string
  tipo_despesa_id: string
  subtipo_nome: string
  pipefy_record_id: string | null
  pipefy_table_id: string
  sincronizado: boolean
  ultima_sincronizacao: string | null
}

export default function SubtiposDespesaTab() {
  const [subtipos, setSubtipos] = useState<SubtipoDespesa[]>([])
  const [databases, setDatabases] = useState<DatabaseItem[]>([])
  const [vinculacoes, setVinculacoes] = useState<SubtipoDatabaseVinculado[]>([])
  const [subtiposSincronizados, setSubtiposSincronizados] = useState<SubtipoSincronizado[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingDatabases, setLoadingDatabases] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [databaseSearchTerm, setDatabaseSearchTerm] = useState('')
  const [areaSelecionada, setAreaSelecionada] = useState<string | null>(null) // Agora armazena centro_custo_id
  const [databaseSelecionado, setDatabaseSelecionado] = useState<string>('')
  const [databaseName, setDatabaseName] = useState('')
  const [databaseDescription, setDatabaseDescription] = useState('')
  const [saving, setSaving] = useState(false)
  const [creating, setCreating] = useState(false)
  const [creatingAll, setCreatingAll] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  // Progresso da criação em lote
  interface BulkLogEntry {
    area: string
    status: 'pending' | 'creating' | 'success' | 'error' | 'cancelled'
    message?: string
  }
  const [bulkProgress, setBulkProgress] = useState<{
    total: number
    done: number
    logs: BulkLogEntry[]
  } | null>(null)
  const cancelBulkRef = useRef(false)
  const logsEndRef = useRef<HTMLDivElement>(null)
  const [areasBase, setAreasBase] = useState<{ id: string; nome: string; nivel: string | null }[]>([])
  const [loadingAreasBase, setLoadingAreasBase] = useState(false)
  const [lastSyncResult, setLastSyncResult] = useState<{
    scope: 'global' | 'area'
    centroCustoId?: string
    centroNome?: string
    synced: number
    errors: string[]
    message?: string
  } | null>(null)

  useEffect(() => {
    fetchSubtipos()
    fetchDatabases()
    fetchAreasBase()
    fetchVinculacoes()
    fetchSubtiposSincronizados()
  }, [])

  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [bulkProgress?.logs])

  const fetchSubtipos = async () => {
    setLoading(true)
    try {
      // Usar a função RPC para consultar a view (muito mais eficiente!)
      const { data, error } = await supabase.rpc('solis_get_subtipos_despesa_por_area')

      if (error) throw error

      // Converter os dados para o formato esperado
      const subtiposData: SubtipoDespesa[] = (data || []).map((item: any) => ({
        centro_custo_id: item.centro_custo_id,
        centro_resultado: item.centro_resultado,
        centro_resultado_original: item.centro_resultado_original,
        subtipo_despesa: item.subtipo_despesa,
        quantidade_registros: Number(item.quantidade_registros) || 0,
        valor_total: Number(item.valor_total) || 0,
      }))

      setSubtipos(subtiposData)
    } catch (error: any) {
      console.error('Erro ao buscar subtipos:', error)
      toast.error('Erro ao buscar subtipos de despesa: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  const fetchDatabases = async () => {
    setLoadingDatabases(true)
    try {
      const data = await pipefyApi.listDatabases({})
      if (data.success && data.databases) {
        setDatabases(data.databases)
      }
    } catch (error: any) {
      console.error('Erro ao buscar databases:', error)
      toast.error('Erro ao buscar databases do Pipefy')
    } finally {
      setLoadingDatabases(false)
    }
  }

  const fetchAreasBase = async () => {
    setLoadingAreasBase(true)
    try {
      const { data, error } = await supabase
        .from('solis_centros_custo')
        .select('id, nome, nivel')
        .eq('ativo', true)
        .order('nome', { ascending: true })

      if (error) throw error
      const normalizadas = (data || []).map((row: any) => ({
        id: row.id as string,
        nome: row.nome as string,
        nivel: (row.nivel as string | null) ?? null,
      }))
      setAreasBase(normalizadas)
    } catch (error: any) {
      console.error('Erro ao buscar áreas base:', error)
    } finally {
      setLoadingAreasBase(false)
    }
  }

  const fetchVinculacoes = async () => {
    try {
      // Buscar vinculações diretamente da tabela solis_centros_custo
      const { data: centrosCusto, error } = await supabase
        .from('solis_centros_custo')
        .select('id, pipefy_subtipos_table_id')
        .not('pipefy_subtipos_table_id', 'is', null)

      if (error) throw error
      
      // Converter para o formato esperado
      const vinculacoesData: SubtipoDatabaseVinculado[] = (centrosCusto || [])
        .filter(cc => cc.pipefy_subtipos_table_id)
        .map(cc => ({
          id: cc.id, // Usar o ID do centro de custo como ID da vinculação
          centro_custo_id: cc.id,
          pipefy_table_id: cc.pipefy_subtipos_table_id!,
          pipefy_table_name: null, // Será preenchido quando buscar os databases
        }))
      
      setVinculacoes(vinculacoesData)
    } catch (error: any) {
      console.error('Erro ao buscar vinculações:', error)
    }
  }

  const fetchSubtiposSincronizados = async () => {
    try {
      const { data, error } = await supabase
        .from('solis_subtipos_sincronizados')
        .select('*')

      if (error) throw error
      setSubtiposSincronizados(data || [])
    } catch (error: any) {
      console.error('Erro ao buscar subtipos sincronizados:', error)
    }
  }

  // Agrupar subtipos por área (centro de custo)
  // Filtrar apenas subtipos que têm centro_custo_id
  const subtiposComCentroCusto = subtipos.filter(s => s.centro_custo_id !== null)
  
  const subtiposPorArea = subtiposComCentroCusto.reduce((acc, subtipo) => {
    const key = subtipo.centro_custo_id!
    if (!acc[key]) {
      acc[key] = {
        centro_custo_id: subtipo.centro_custo_id!,
        centro_resultado: subtipo.centro_resultado,
        subtipos: [],
      }
    }
    acc[key].subtipos.push(subtipo)
    return acc
  }, {} as Record<string, { centro_custo_id: string; centro_resultado: string; subtipos: SubtipoDespesa[]; nivel?: string | null }>)
  const areasComSubtipos = Object.values(subtiposPorArea)

  const areas = (areasBase.length > 0
    ? areasBase.map((base) => {
        const existente = subtiposPorArea[base.id]
        if (existente) {
          return {
            ...existente,
            nivel: base.nivel,
          }
        }
        return {
          centro_custo_id: base.id,
          centro_resultado: base.nome,
          subtipos: [] as SubtipoDespesa[],
          nivel: base.nivel,
        }
      })
    : areasComSubtipos)

  const getAreaNivel = (centroCustoId: string): string | null => {
    const base = areasBase.find((a) => a.id === centroCustoId)
    return base?.nivel ?? null
  }

  const getAreaLabel = (area: { centro_custo_id: string; centro_resultado: string }) => {
    const nivel = getAreaNivel(area.centro_custo_id)
    if (area.centro_resultado === 'Operações' && nivel) {
      return `${area.centro_resultado} (${nivel})`
    }
    return area.centro_resultado
  }

  const getDatabaseNameForArea = (area: { centro_custo_id: string; centro_resultado: string }) => {
    const label = getAreaLabel(area)
    return `Subtipos - ${label}`
  }

  // Filtrar áreas
  const areasFiltradas = areas.filter((area) => {
    const label = getAreaLabel(area)
    return label.toLowerCase().includes(searchTerm.toLowerCase())
  })

  const getVinculacao = (centroCustoId: string) => {
    return vinculacoes.find((v) => v.centro_custo_id === centroCustoId)
  }

  const getDatabaseInfo = (tableId: string | null | undefined) => {
    if (!tableId) return null
    return databases.find((db) => db.id === tableId) || null
  }

  const handleVincular = async () => {
    if (!areaSelecionada || !databaseSelecionado) {
      toast.error('Selecione uma área e um database')
      return
    }

    setSaving(true)
    try {
      const area = areas.find((a) => a.centro_custo_id === areaSelecionada)
      const database = databases.find((db) => db.id === databaseSelecionado)

      if (!area?.centro_custo_id) {
        toast.error('Área não possui centro de custo vinculado')
        return
      }

      // Atualizar diretamente na tabela solis_centros_custo
      const { error } = await supabase
        .from('solis_centros_custo')
        .update({
          pipefy_subtipos_table_id: databaseSelecionado,
        })
        .eq('id', area.centro_custo_id)

      if (error) throw error

      toast.success('Database vinculado com sucesso!')
      setDialogOpen(false)
      setAreaSelecionada(null)
      setDatabaseSelecionado('')
      fetchVinculacoes()
    } catch (error: any) {
      console.error('Erro ao vincular database:', error)
      toast.error('Erro ao vincular database: ' + error.message)
    } finally {
      setSaving(false)
    }
  }

  const handleCriarEVincular = async () => {
    if (!areaSelecionada || !databaseName.trim()) {
      toast.error('Informe o nome do database')
      return
    }

    setCreating(true)
    try {
      const area = areas.find((a) => a.centro_custo_id === areaSelecionada)

      // Garantir que o nome comece com "Subtipos - "
      let finalDatabaseName = databaseName.trim()
      if (!finalDatabaseName.startsWith('Subtipos - ')) {
        const label = area ? getAreaLabel(area) : finalDatabaseName
        finalDatabaseName = `Subtipos - ${label}`
      }

      // Criar database no Pipefy
      const createResult = await pipefyApi.createDatabase({
        name: finalDatabaseName,
        description: databaseDescription.trim() || undefined,
      })

      if (!createResult.success || !createResult.table) {
        throw new Error(createResult.error || 'Erro ao criar database')
      }

      const newTableId = createResult.table.id

      // Criar os campos no database recém-criado
      const camposParaCriar: CampoConfig[] = [
        {
          label: 'Nome do Subtipo',
          type: 'short_text',
          required: true,
          description: 'Nome ou descrição do subtipo de despesa',
        },
      ]

      // Adicionar campo de conexão com database de tipo de despesa
      camposParaCriar.push({
        label: 'Tipo de Despesa',
        type: 'connector_table',
        required: false,
        description: 'Conexão com o database de tipos de despesa',
        connectedRepoId: DATABASE_TIPO_DESPESA_ID,
      })

      // Adicionar campo de conexão com database de centro de custo
      camposParaCriar.push({
        label: 'Centro de Custo',
        type: 'connector_table',
        required: false,
        description: 'Conexão com o database de centros de custo',
        connectedRepoId: DATABASE_CENTRO_CUSTO_ID,
      })

      // Criar os campos
      for (const campo of camposParaCriar) {
        try {
          if (campo.type === 'connector_table') {
            await pipefyApi.createField({
              tableId: newTableId,
              type: 'connector',
              label: campo.label,
              required: campo.required || false,
              description: campo.description,
              connectedRepoId: campo.connectedRepoId,
            })
          } else {
            await pipefyApi.createField({
              tableId: newTableId,
              type: campo.type,
              label: campo.label,
              required: campo.required || false,
              description: campo.description,
            })
          }
        } catch (fieldError: any) {
          console.warn(`Erro ao criar campo "${campo.label}":`, fieldError)
          // Continuar criando os outros campos mesmo se um falhar
        }
      }

      if (!area?.centro_custo_id) {
        throw new Error('Área não possui centro de custo vinculado')
      }

      // Vincular ao banco diretamente na tabela solis_centros_custo
      const { error } = await supabase
        .from('solis_centros_custo')
        .update({
          pipefy_subtipos_table_id: newTableId,
        })
        .eq('id', area.centro_custo_id)

      if (error) throw error

      toast.success(`Database "${createResult.table.name}" criado com ${camposParaCriar.length} campo(s) e vinculado com sucesso!`)
      setCreateDialogOpen(false)
      setAreaSelecionada(null)
      setDatabaseName('')
      setDatabaseDescription('')
      fetchDatabases()
      fetchVinculacoes()
    } catch (error: any) {
      console.error('Erro ao criar database:', error)
      toast.error('Erro ao criar database: ' + error.message)
    } finally {
      setCreating(false)
    }
  }

  const handleCriarDatabasesParaAreasSemDatabase = async () => {
    const areasSemDatabase = areas.filter((a) => !getVinculacao(a.centro_custo_id))

    if (areasSemDatabase.length === 0) {
      toast.info('Todas as áreas já possuem um database vinculado.')
      return
    }

    cancelBulkRef.current = false
    setCreatingAll(true)

    const initialLogs: BulkLogEntry[] = areasSemDatabase.map((a) => ({
      area: getAreaLabel(a),
      status: 'pending' as const,
    }))
    setBulkProgress({ total: areasSemDatabase.length, done: 0, logs: initialLogs })

    const updateLog = (index: number, update: Partial<BulkLogEntry>) => {
      setBulkProgress((prev) => {
        if (!prev) return prev
        const logs = [...prev.logs]
        logs[index] = { ...logs[index], ...update }
        const done = logs.filter((l) => l.status === 'success' || l.status === 'error' || l.status === 'cancelled').length
        return { ...prev, done, logs }
      })
    }

    let criados = 0
    let errosCount = 0

    for (let i = 0; i < areasSemDatabase.length; i++) {
      if (cancelBulkRef.current) {
        for (let j = i; j < areasSemDatabase.length; j++) {
          updateLog(j, { status: 'cancelled', message: 'Cancelado pelo usuário' })
        }
        break
      }

      const area = areasSemDatabase[i]
      updateLog(i, { status: 'creating', message: 'Criando database...' })

      try {
        const finalDatabaseName = getDatabaseNameForArea(area).trim()

          const createResult = await pipefyApi.createDatabase({
            name: finalDatabaseName,
            description: `Subtipos de despesa da área ${getAreaLabel(area)}`,
          })

        if (!createResult.success || !createResult.table) {
          throw new Error(createResult.error || 'Erro ao criar database')
        }

        const newTableId = createResult.table.id
        updateLog(i, { message: `Database criado (${newTableId}). Criando campos...` })

        if (cancelBulkRef.current) {
          updateLog(i, { status: 'cancelled', message: `Database criado mas cancelado antes dos campos` })
          for (let j = i + 1; j < areasSemDatabase.length; j++) {
            updateLog(j, { status: 'cancelled', message: 'Cancelado pelo usuário' })
          }
          break
        }

        const camposParaCriar: CampoConfig[] = [
          { label: 'Nome do Subtipo', type: 'short_text', required: true, description: 'Nome ou descrição do subtipo de despesa' },
          { label: 'Tipo de Despesa', type: 'connector_table', required: false, description: 'Conexão com o database de tipos de despesa', connectedRepoId: DATABASE_TIPO_DESPESA_ID },
          { label: 'Centro de Custo', type: 'connector_table', required: false, description: 'Conexão com o database de centros de custo', connectedRepoId: DATABASE_CENTRO_CUSTO_ID },
        ]

        const campoErros: string[] = []
        for (const campo of camposParaCriar) {
          try {
            await pipefyApi.createField({
              tableId: newTableId,
              type: campo.type === 'connector_table' ? 'connector' : campo.type,
              label: campo.label,
              required: campo.required || false,
              description: campo.description,
              ...(campo.connectedRepoId ? { connectedRepoId: campo.connectedRepoId } : {}),
            })
          } catch (fieldError: any) {
            campoErros.push(`campo "${campo.label}": ${fieldError?.message || fieldError}`)
          }
        }

        updateLog(i, { message: 'Vinculando ao centro de custo...' })

        const { error } = await supabase
          .from('solis_centros_custo')
          .update({ pipefy_subtipos_table_id: newTableId })
          .eq('id', area.centro_custo_id)

        if (error) throw error

        criados += 1
        const msg = campoErros.length > 0
          ? `Database criado e vinculado (${campoErros.length} campo(s) com erro)`
          : `Database criado e vinculado com sucesso`
        updateLog(i, { status: 'success', message: msg })
      } catch (e: any) {
        errosCount += 1
        console.error('Erro ao criar/vincular database para área', area.centro_resultado, e)
        updateLog(i, { status: 'error', message: e?.message || String(e) })
      }
    }

    const cancelled = cancelBulkRef.current
    if (criados > 0) {
      toast.success(`${criados} database(s) criado(s) com sucesso.${cancelled ? ' Processo cancelado.' : ''}`)
    }
    if (errosCount > 0) {
      toast.warning(`${errosCount} erro(s) durante a criação.`)
    }
    if (cancelled && criados === 0) {
      toast.info('Processo cancelado.')
    }

    fetchDatabases()
    fetchVinculacoes()
    setCreatingAll(false)
  }

  const handleCancelBulk = () => {
    cancelBulkRef.current = true
  }

  const handleCloseBulkProgress = () => {
    setBulkProgress(null)
  }

  const handleSyncSubtipos = async () => {
    setSyncing(true)
    try {
      const result = await pipefyApi.syncSubtipos({})
      
      if (result.success) {
        setLastSyncResult({
          scope: 'global',
          synced: result.synced ?? 0,
          errors: result.errors || [],
          message: result.message,
        })
        toast.success(`Sincronização concluída! ${result.synced} subtipo(s) sincronizado(s).`)
        if (result.errors && result.errors.length > 0) {
          console.warn('Erros durante sincronização:', result.errors)
          toast.warning(`${result.errors.length} erro(s) durante a sincronização. Verifique o debug acima.`)
        }
        // Atualizar lista de subtipos sincronizados
        fetchSubtiposSincronizados()
      } else {
        throw new Error(result.error || 'Erro desconhecido na sincronização')
      }
    } catch (error: any) {
      console.error('Erro ao sincronizar subtipos:', error)
      toast.error('Erro ao sincronizar subtipos: ' + error.message)
    } finally {
      setSyncing(false)
    }
  }

  const handleSyncSubtiposArea = async (area: { centro_custo_id: string; centro_resultado: string }) => {
    const centroCustoId = area.centro_custo_id
    setSyncing(true)
    try {
      const result = await pipefyApi.syncSubtipos({ centroCustoId })
      
      if (result.success) {
        const msgPrefix = 'Sincronização da área concluída!'
        toast.success(
          `${msgPrefix} ${result.synced} subtipo(s) sincronizado(s).`
        )
        if (result.errors && result.errors.length > 0) {
          console.warn('Erros durante sincronização por área:', result.errors)
          toast.warning(`${result.errors.length} erro(s) durante a sincronização. Verifique o debug acima.`)
        }
        setLastSyncResult({
          scope: 'area',
          centroCustoId,
          centroNome: getAreaLabel(area),
          synced: result.synced ?? 0,
          errors: result.errors || [],
          message: result.message,
        })
        fetchSubtiposSincronizados()
      } else {
        throw new Error(result.error || 'Erro desconhecido na sincronização por área')
      }
    } catch (error: any) {
      console.error('Erro ao sincronizar subtipos da área:', error)
      toast.error('Erro ao sincronizar subtipos da área: ' + error.message)
    } finally {
      setSyncing(false)
    }
  }

  const handleVerifySubtipos = async () => {
    setSyncing(true)
    try {
      const result = await pipefyApi.verifySubtipos({})
      
      if (result.success) {
        toast.success(`Verificação concluída! ${result.verified} registro(s) verificado(s) e atualizado(s).`)
        if (result.errors && result.errors.length > 0) {
          console.warn('Erros durante verificação:', result.errors)
          toast.warning(`${result.errors.length} erro(s) durante a verificação. Verifique o console.`)
        }
        // Atualizar lista de subtipos sincronizados
        fetchSubtiposSincronizados()
      } else {
        throw new Error(result.error || 'Erro desconhecido na verificação')
      }
    } catch (error: any) {
      console.error('Erro ao verificar subtipos:', error)
      toast.error('Erro ao verificar subtipos: ' + error.message)
    } finally {
      setSyncing(false)
    }
  }

  // Verificar se um subtipo está sincronizado
  // Usa apenas centro_custo_id e subtipo_nome, já que a view não retorna tipo_despesa_id
  const isSubtipoSincronizado = (centroCustoId: string, subtipoNome: string) => {
    return subtiposSincronizados.some(
      s => s.centro_custo_id === centroCustoId &&
           s.subtipo_nome === subtipoNome &&
           s.sincronizado === true
    )
  }

  // Contar subtipos sincronizados por área
  const getSubtiposSincronizadosCount = (centroCustoId: string) => {
    return subtiposSincronizados.filter(
      s => s.centro_custo_id === centroCustoId && s.sincronizado === true
    ).length
  }

  const handleDesvincular = async (vinculacao: SubtipoDatabaseVinculado) => {
    try {
      // Remover vinculação atualizando a coluna para NULL
      const { error } = await supabase
        .from('solis_centros_custo')
        .update({
          pipefy_subtipos_table_id: null,
        })
        .eq('id', vinculacao.centro_custo_id)

      if (error) throw error

      toast.success('Database desvinculado com sucesso!')
      fetchVinculacoes()
    } catch (error: any) {
      console.error('Erro ao desvincular database:', error)
      toast.error('Erro ao desvincular database: ' + error.message)
    }
  }

  const columns: DataTableColumn<typeof areas[0]>[] = [
    {
      key: 'centro_resultado',
      label: 'Área / Centro de Custo',
      render: (row) => (
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{getAreaLabel(row)}</span>
        </div>
      ),
    },
    {
      key: 'subtipos',
      label: 'Quantidade de Subtipos',
      render: (row) => {
        const total = row.subtipos.length
        const sincronizados = row.subtipos.filter((s: SubtipoDespesa) => 
          isSubtipoSincronizado(row.centro_custo_id, s.subtipo_despesa)
        ).length
        
        return (
          <div className="flex flex-col gap-1">
            <Badge variant="outline">
              {total} subtipo{total !== 1 ? 's' : ''}
            </Badge>
            {sincronizados > 0 && (
              <Badge variant="default" className="text-xs">
                ✓ {sincronizados} sincronizado{sincronizados !== 1 ? 's' : ''}
              </Badge>
            )}
          </div>
        )
      },
    },
    {
      key: 'database',
      label: 'Database Vinculado',
      render: (row) => {
        const vinculacao = getVinculacao(row.centro_custo_id)
        if (vinculacao) {
          const database = getDatabaseInfo(vinculacao.pipefy_table_id)
          return (
            <div className="flex items-center gap-2">
              <Database className="h-4 w-4 text-primary" />
              <span className="text-sm">{database?.name || vinculacao.pipefy_table_name || 'N/A'}</span>
            </div>
          )
        }
        return (
          <Badge variant="outline" className="text-muted-foreground">
            Não vinculado
          </Badge>
        )
      },
    },
    {
      key: 'status',
      label: 'Status Sincronização',
      render: (row) => {
        const vinculacao = getVinculacao(row.centro_custo_id)
        if (!vinculacao) {
          return (
            <Badge variant="outline" className="text-muted-foreground">
              Sem database
            </Badge>
          )
        }

        const total = row.subtipos.length
        const sincronizados = row.subtipos.filter((s: SubtipoDespesa) => 
          isSubtipoSincronizado(row.centro_custo_id, s.subtipo_despesa)
        ).length
        const pendentes = total - sincronizados

        if (sincronizados === 0) {
          return (
            <Badge variant="destructive" className="w-fit">
              <X className="h-3 w-3 mr-1" />
              Não sincronizado
            </Badge>
          )
        }

        if (sincronizados === total) {
          return (
            <Badge variant="default" className="bg-green-600 w-fit">
              <RefreshCw className="h-3 w-3 mr-1" />
              Totalmente sincronizado
            </Badge>
          )
        }

        return (
          <div className="flex flex-col gap-1">
            <Badge variant="default" className="bg-green-600 w-fit">
              ✓ {sincronizados} sincronizado{sincronizados !== 1 ? 's' : ''}
            </Badge>
            <Badge variant="outline" className="text-orange-600 w-fit">
              ⏳ {pendentes} pendente{pendentes !== 1 ? 's' : ''}
            </Badge>
          </div>
        )
      },
    },
    {
      key: 'actions',
      label: 'Ações',
      className: 'text-right',
      render: (row) => {
        const vinculacao = getVinculacao(row.centro_custo_id)
        return (
          <div className="flex items-center justify-end gap-2">
            {vinculacao && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleSyncSubtiposArea(row)}
                disabled={syncing || row.subtipos.length === 0}
                title="Sincronizar apenas esta área"
              >
                <RefreshCw className="h-3 w-3 mr-1" />
                Sync área
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setAreaSelecionada(row.centro_custo_id)
                setDatabaseSelecionado(vinculacao?.pipefy_table_id || '')
                setDialogOpen(true)
              }}
            >
              <Link2 className="h-3 w-3 mr-1" />
              {vinculacao ? 'Alterar' : 'Vincular'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setAreaSelecionada(row.centro_custo_id)
                setDatabaseName(getDatabaseNameForArea(row))
                setCreateDialogOpen(true)
              }}
            >
              <Plus className="h-3 w-3 mr-1" />
              Criar Database
            </Button>
            {vinculacao && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDesvincular(vinculacao)}
                title="Desvincular database"
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>
        )
      },
    },
  ]

  // Estatísticas
  const totalAreas = areas.length
  const areasVinculadas = areas.filter((area) => getVinculacao(area.centro_custo_id)).length
  const areasNaoVinculadas = totalAreas - areasVinculadas
  
  // Contar subtipos sincronizados
  const totalSubtipos = subtipos.length
  const subtiposSincronizadosCount = subtiposSincronizados.filter(s => s.sincronizado).length
  const subtiposNaoSincronizadosCount = totalSubtipos - subtiposSincronizadosCount

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h3 className="text-2xl font-bold tracking-tight">Subtipos de Despesa por Área</h3>
          <p className="text-muted-foreground text-sm">
            Crie e vincule databases do Pipefy para gerenciar subtipos de despesa por área
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={fetchDatabases}
            disabled={loadingDatabases}
            title="Recarrega a lista de databases disponíveis no Pipefy."
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${loadingDatabases ? 'animate-spin' : ''}`} />
            Atualizar Databases
          </Button>
          <Button
            variant="outline"
            onClick={fetchSubtipos}
            disabled={loading}
            title="Recarrega os subtipos de despesa a partir da view de orçamento."
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          <Button
            variant="outline"
            onClick={handleVerifySubtipos}
            disabled={syncing}
            title="Lê os registros já existentes nos databases de subtipos e marca como sincronizados os que já estão no Pipefy."
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
            Verificar Registros Existentes
          </Button>
          <Button
            onClick={handleSyncSubtipos}
            disabled={syncing}
            title="Cria ou atualiza todos os registros de subtipos em todos os databases vinculados."
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Sincronizando...' : 'Sincronizar Subtipos'}
          </Button>
          <Button
            onClick={handleCriarDatabasesParaAreasSemDatabase}
            disabled={creatingAll || areas.length === 0}
            title="Cria automaticamente um database de subtipos no Pipefy para cada área que ainda não tem um vinculado."
          >
            <Plus className={`mr-2 h-4 w-4 ${creatingAll ? 'animate-spin' : ''}`} />
            {creatingAll ? 'Criando databases...' : 'Criar databases para áreas sem database'}
          </Button>
        </div>
      </div>

      {/* Debug da última sincronização de subtipos */}
      {lastSyncResult && (
        <Card className="border-primary/30">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <RefreshCw className="h-4 w-4 text-primary" />
                  <CardTitle className="text-base">
                    {lastSyncResult.scope === 'global'
                      ? 'Sincronização de subtipos (todas as áreas)'
                      : `Sincronização de subtipos – ${lastSyncResult.centroNome ?? 'Área'}`}
                  </CardTitle>
                </div>
                <CardDescription>
                  {`Subtipos sincronizados: ${lastSyncResult.synced}.`}
                  {lastSyncResult.errors.length > 0 &&
                    ` Ocorreram ${lastSyncResult.errors.length} erro(s). Veja detalhes abaixo.`}
                </CardDescription>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setLastSyncResult(null)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          {lastSyncResult.errors.length > 0 && (
            <CardContent className="pt-0">
              <ScrollArea className="h-[160px] rounded-md border p-3 bg-muted/30">
                <div className="space-y-1 font-mono text-xs">
                  {lastSyncResult.errors.map((err, idx) => (
                    <div key={idx} className="text-destructive">
                      • {err}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          )}
        </Card>
      )}

      {/* KPIs */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Áreas</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalAreas}</div>
            <p className="text-xs text-muted-foreground">Áreas com subtipos</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Vinculadas</CardTitle>
            <Link2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{areasVinculadas}</div>
            <p className="text-xs text-muted-foreground">
              {totalAreas > 0 ? ((areasVinculadas / totalAreas) * 100).toFixed(1) : 0}% vinculadas
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Subtipos</CardTitle>
            <Database className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalSubtipos}</div>
            <p className="text-xs text-muted-foreground">Subtipos de despesa</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Sincronizados</CardTitle>
            <RefreshCw className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{subtiposSincronizadosCount}</div>
            <p className="text-xs text-muted-foreground">
              {totalSubtipos > 0 ? ((subtiposSincronizadosCount / totalSubtipos) * 100).toFixed(1) : 0}% sincronizados
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pendentes</CardTitle>
            <X className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{subtiposNaoSincronizadosCount}</div>
            <p className="text-xs text-muted-foreground">Aguardando sincronização</p>
          </CardContent>
        </Card>
      </div>

      {/* Painel de progresso da criação em lote */}
      {bulkProgress && (
        <Card className="border-primary/30">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {creatingAll ? (
                  <Loader2 className="h-5 w-5 animate-spin text-primary" />
                ) : bulkProgress.logs.some((l) => l.status === 'error') ? (
                  <XCircle className="h-5 w-5 text-destructive" />
                ) : (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                )}
                <CardTitle className="text-base">
                  {creatingAll
                    ? `Criando databases... (${bulkProgress.done}/${bulkProgress.total})`
                    : `Criação finalizada (${bulkProgress.logs.filter((l) => l.status === 'success').length}/${bulkProgress.total} criados)`}
                </CardTitle>
              </div>
              <div className="flex items-center gap-2">
                {creatingAll && (
                  <Button variant="destructive" size="sm" onClick={handleCancelBulk}>
                    <Square className="mr-1.5 h-3 w-3" />
                    Cancelar
                  </Button>
                )}
                {!creatingAll && (
                  <Button variant="ghost" size="sm" onClick={handleCloseBulkProgress}>
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
            <Progress
              value={bulkProgress.total > 0 ? (bulkProgress.done / bulkProgress.total) * 100 : 0}
              className="mt-2"
            />
          </CardHeader>
          <CardContent className="pt-0">
            <ScrollArea className="h-[200px] rounded-md border p-3 bg-muted/30">
              <div className="space-y-1.5 font-mono text-xs">
                {bulkProgress.logs.map((log, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    {log.status === 'pending' && (
                      <span className="text-muted-foreground mt-0.5">○</span>
                    )}
                    {log.status === 'creating' && (
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-primary mt-0.5 shrink-0" />
                    )}
                    {log.status === 'success' && (
                      <CheckCircle2 className="h-3.5 w-3.5 text-green-600 mt-0.5 shrink-0" />
                    )}
                    {log.status === 'error' && (
                      <XCircle className="h-3.5 w-3.5 text-destructive mt-0.5 shrink-0" />
                    )}
                    {log.status === 'cancelled' && (
                      <Square className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
                    )}
                    <span className={
                      log.status === 'error' ? 'text-destructive' :
                      log.status === 'cancelled' ? 'text-muted-foreground' :
                      log.status === 'success' ? 'text-green-700 dark:text-green-400' :
                      log.status === 'creating' ? 'text-primary' :
                      'text-muted-foreground'
                    }>
                      <span className="font-semibold">{log.area}</span>
                      {log.message && <span className="ml-1.5">— {log.message}</span>}
                    </span>
                  </div>
                ))}
                <div ref={logsEndRef} />
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Tabela */}
      <Card>
        <CardHeader>
          <CardTitle>Áreas e Subtipos</CardTitle>
          <CardDescription>
            Lista de áreas com seus subtipos de despesa e databases vinculados
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar área..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <DataTable
              columns={columns}
              data={areasFiltradas}
              loading={loading}
              emptyText="Nenhuma área encontrada."
              searchable={false}
            />
          </div>
        </CardContent>
      </Card>

      {/* Dialog para vincular database existente */}
      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open)
          if (!open) setDatabaseSearchTerm('')
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Vincular Database do Pipefy</DialogTitle>
            <DialogDescription>
              Selecione o database do Pipefy para vincular à área "{areaSelecionada ? getAreaLabel(areas.find(a => a.centro_custo_id === areaSelecionada)!) || areaSelecionada : areaSelecionada}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Database do Pipefy</label>
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar database..."
                  value={databaseSearchTerm}
                  onChange={(e) => setDatabaseSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select
                value={databaseSelecionado}
                onValueChange={setDatabaseSelecionado}
                disabled={loadingDatabases}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um database..." />
                </SelectTrigger>
                <SelectContent>
                  {(() => {
                    const filtrados = databases.filter(
                      (db) =>
                        !databaseSearchTerm.trim() ||
                        db.name.toLowerCase().includes(databaseSearchTerm.toLowerCase().trim())
                    )
                    if (filtrados.length === 0) {
                      return (
                        <div className="py-6 text-center text-sm text-muted-foreground">
                          Nenhum database encontrado.
                        </div>
                      )
                    }
                    return filtrados.map((db) => (
                      <SelectItem key={db.id} value={db.id}>
                        <div className="flex flex-col">
                          <span>{db.name}</span>
                          {db.description && (
                            <span className="text-xs text-muted-foreground">{db.description}</span>
                          )}
                        </div>
                      </SelectItem>
                    ))
                  })()}
                </SelectContent>
              </Select>
            </div>
            {areaSelecionada && getVinculacao(areaSelecionada) && (
              <div className="p-3 bg-muted rounded-md">
                <p className="text-sm text-muted-foreground">
                  <strong>Database atual:</strong> {getDatabaseInfo(getVinculacao(areaSelecionada)?.pipefy_table_id)?.name || getVinculacao(areaSelecionada)?.pipefy_table_name || 'N/A'}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={handleVincular} disabled={saving || !databaseSelecionado}>
              {saving ? 'Salvando...' : 'Vincular'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog para criar e vincular database */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Criar e Vincular Database</DialogTitle>
            <DialogDescription>
              Crie um novo database no Pipefy para a área "{areaSelecionada ? getAreaLabel(areas.find(a => a.centro_custo_id === areaSelecionada)!) || areaSelecionada : areaSelecionada}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">
                Nome do Database <span className="text-red-500">*</span>
              </label>
              <Input
                placeholder="Ex: Admin - Subtipos de Despesa"
                value={databaseName}
                onChange={(e) => setDatabaseName(e.target.value)}
                disabled={creating}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Descrição (Opcional)</label>
              <Input
                placeholder="Descrição do database..."
                value={databaseDescription}
                onChange={(e) => setDatabaseDescription(e.target.value)}
                disabled={creating}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)} disabled={creating}>
              Cancelar
            </Button>
            <Button onClick={handleCriarEVincular} disabled={creating || !databaseName.trim()}>
              {creating ? 'Criando...' : 'Criar e Vincular'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

